import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, MessageCircle, Phone, Mail, Calendar, FileText, ChevronRight, Plus, Clock, MapPin, Download, X } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const formatTime = (dt) => { try { return format(new Date(dt), "HH'h'mm"); } catch { return ''; } };
const formatDate = (dt) => { try { return format(new Date(dt), "d MMM yyyy", { locale: fr }); } catch { return ''; } };

const APT_COLORS = {
  'seance': { bg: '#E0F2F0', text: '#2E665E', dot: '#4A9B8F' },
  'bilan': { bg: '#DBEAFE', text: '#1E3A8A', dot: '#6B9FD4' },
  'reunion': { bg: '#F3E8FF', text: '#6B21A8', dot: '#A78BCA' },
  'autre': { bg: '#F5F5F4', text: '#57534E', dot: '#94A3B8' },
};

export default function PatientProfessionalDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pro, setPro] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('planning');
  const [showAddApt, setShowAddApt] = useState(false);
  const [savingApt, setSavingApt] = useState(false);
  const [aptForm, setAptForm] = useState({ title: '', appointment_type: 'seance', date: '', start_time: '', end_time: '', location: '' });

  useEffect(() => { if (user) loadData(); }, [user, id]);

  const loadData = async () => {
    try {
      const { data: proData } = await supabase.from('patient_professionals').select('*').eq('id', id).single();
      setPro(proData);

      const { data: apts } = await supabase.from('appointments')
        .select('*')
        .eq('patient_id', user.id)
        .order('start_datetime', { ascending: false })
        .limit(20);
      setAppointments(apts || []);

      const { data: docs } = await supabase.from('documents').select('*').eq('patient_id', user.id).order('created_at', { ascending: false });
      setDocuments(docs || []);
    } catch (error) {
      console.error('Error loading:', error);
    } finally {
      setLoading(false);
    }
  };

  const startConversation = async () => {
    if (!pro?.professional_id) return;
    const { data: existing } = await supabase.from('conversations').select('id').eq('patient_id', user.id).eq('professional_id', pro.professional_id).maybeSingle();
    if (existing) {
      navigate(`/patient/messages/${existing.id}`);
    } else {
      const { data: conv } = await supabase.from('conversations').insert({
        patient_id: user.id,
        professional_id: pro.professional_id,
        pro_name: pro.professional_name,
        last_message_at: new Date().toISOString(),
      }).select().single();
      navigate(`/patient/messages/${conv.id}`);
    }
  };

  const addAppointment = async () => {
    if (!aptForm.title || !aptForm.date || !aptForm.start_time || !aptForm.end_time) return;
    setSavingApt(true);
    try {
      await supabase.from('appointments').insert({
        patient_id: user.id,
        professional_id: pro?.professional_id || null,
        title: aptForm.title,
        appointment_type: aptForm.appointment_type,
        start_datetime: `${aptForm.date}T${aptForm.start_time}:00`,
        end_datetime: `${aptForm.date}T${aptForm.end_time}:00`,
        location: aptForm.location || null,
        status: 'confirmed',
      });
      setShowAddApt(false);
      setAptForm({ title: '', appointment_type: 'seance', date: '', start_time: '', end_time: '', location: '' });
      loadData();
    } catch (error) {
      console.error('Error adding appointment:', error);
    } finally {
      setSavingApt(false);
    }
  };

  if (loading) return <div className="p-5 space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-stone-100 rounded-3xl animate-pulse" />)}</div>;
  if (!pro) return <div className="p-5 text-center text-slate-400">Professionnel introuvable</div>;

  return (
    <div className="pb-6">
      {/* Header */}
      <div className="px-5 pt-5 pb-4">
        <button onClick={() => navigate('/patient/professionnels')} className="flex items-center gap-2 text-slate-400 mb-4">
          <ArrowLeft size={18} /> <span className="text-sm font-heading font-semibold">Mon équipe</span>
        </button>
        <div className="passerelle-card" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, white 60%)' }}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-heading font-bold text-2xl flex-shrink-0"
              style={{ backgroundColor: '#7C6BAE' }}>
              {pro.professional_name?.[0] || 'P'}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-lg text-slate-800">{pro.professional_name}</h1>
              <p className="text-sm text-slate-400 font-body">{pro.profession}</p>
              {pro.phone && <p className="text-xs text-slate-400 font-body mt-1">📞 {pro.phone}</p>}
              {pro.email && <p className="text-xs text-slate-400 font-body">✉️ {pro.email}</p>}
              {pro.address && <p className="text-xs text-slate-400 font-body">📍 {pro.address}</p>}
            </div>
          </div>
          {pro.is_on_passerelle && (
            <button onClick={startConversation}
              className="w-full mt-4 h-10 rounded-2xl font-heading font-semibold text-sm flex items-center justify-center gap-2"
              style={{ backgroundColor: '#4A9B8F', color: 'white' }}>
              <MessageCircle size={15} /> Envoyer un message
            </button>
          )}
          {!pro.is_on_passerelle && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs text-amber-700 font-body">Ce professionnel n'est pas encore sur Passerelle — vous ne pouvez pas lui envoyer de message pour l'instant.</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 mx-5 mb-4 border border-stone-200 rounded-xl overflow-hidden">
        {[{ key: 'planning', label: '📅 Planning' }, { key: 'documents', label: '📄 Documents' }].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 text-xs font-heading font-semibold transition-all ${activeTab === tab.key ? 'text-white' : 'text-slate-500 bg-white'}`}
            style={activeTab === tab.key ? { backgroundColor: '#7C6BAE' } : {}}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="px-5">
        {/* Tab Planning */}
        {activeTab === 'planning' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="section-label">Rendez-vous</p>
              <button onClick={() => setShowAddApt(true)} className="flex items-center gap-1 text-sage-600 font-heading font-semibold text-xs">
                <Plus size={13} /> Ajouter
              </button>
            </div>
            {appointments.length === 0 ? (
              <div className="passerelle-card text-center py-8">
                <Calendar size={24} className="text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400 font-body">Aucun rendez-vous</p>
              </div>
            ) : (
              <div className="space-y-2">
                {appointments.map(apt => {
                  const colors = APT_COLORS[apt.appointment_type] || APT_COLORS['autre'];
                  return (
                    <div key={apt.id} className="passerelle-card flex gap-3">
                      <div className="w-1 rounded-full flex-shrink-0" style={{ backgroundColor: colors.dot }} />
                      <div className="flex-1">
                        <p className="font-heading font-semibold text-sm text-slate-800">{apt.title}</p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 text-xs text-slate-400 font-body">
                            <Clock size={11} /> {formatDate(apt.start_datetime)} · {formatTime(apt.start_datetime)}
                          </span>
                          {apt.location && <span className="flex items-center gap-1 text-xs text-slate-400 font-body"><MapPin size={11} /> {apt.location}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tab Documents */}
        {activeTab === 'documents' && (
          <div>
            <p className="section-label mb-3">Documents partagés</p>
            {documents.length === 0 ? (
              <div className="passerelle-card text-center py-8">
                <FileText size={24} className="text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400 font-body">Aucun document</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map(doc => (
                  <div key={doc.id} className="passerelle-card flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <FileText size={18} className="text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-semibold text-sm text-slate-700 truncate">{doc.title}</p>
                      <p className="text-xs text-slate-400 font-body">{formatDate(doc.created_at)}</p>
                    </div>
                    {doc.file_url && (
                      <a href={doc.file_url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-xl bg-sage-50 flex items-center justify-center flex-shrink-0">
                        <Download size={14} className="text-sage-600" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal ajout RDV */}
      {showAddApt && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end justify-center">
          <div className="w-full max-w-[480px] bg-white rounded-t-3xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg text-slate-800">Nouveau rendez-vous</h3>
              <button onClick={() => setShowAddApt(false)} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center"><X size={16} className="text-slate-500" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1">Titre *</label>
                <input type="text" value={aptForm.title} onChange={e => setAptForm(p => ({...p, title: e.target.value}))}
                  placeholder="Ex: Séance orthophonie" className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
              </div>
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1">Type</label>
                <select value={aptForm.appointment_type} onChange={e => setAptForm(p => ({...p, appointment_type: e.target.value}))}
                  className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body">
                  <option value="seance">Séance</option>
                  <option value="bilan">Bilan</option>
                  <option value="reunion">Réunion</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1">Date *</label>
                <input type="date" value={aptForm.date} onChange={e => setAptForm(p => ({...p, date: e.target.value}))}
                  className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1">Début *</label>
                  <input type="time" value={aptForm.start_time} onChange={e => setAptForm(p => ({...p, start_time: e.target.value}))}
                    className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
                </div>
                <div>
                  <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1">Fin *</label>
                  <input type="time" value={aptForm.end_time} onChange={e => setAptForm(p => ({...p, end_time: e.target.value}))}
                    className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-heading font-bold uppercase tracking-widest text-slate-400 mb-1">Lieu</label>
                <input type="text" value={aptForm.location} onChange={e => setAptForm(p => ({...p, location: e.target.value}))}
                  placeholder="Ex: Cabinet rue de la Paix" className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-xl text-sm font-body" />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowAddApt(false)} className="flex-1 h-11 bg-stone-100 text-slate-600 rounded-2xl font-heading font-semibold text-sm">Annuler</button>
              <button onClick={addAppointment} disabled={savingApt || !aptForm.title || !aptForm.date || !aptForm.start_time || !aptForm.end_time}
                className="flex-1 h-11 rounded-2xl font-heading font-semibold text-sm disabled:opacity-40"
                style={{ backgroundColor: '#4A9B8F', color: 'white' }}>
                {savingApt ? '...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
